import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const ManageBooksPage = () => {
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newBook, setNewBook] = useState({
    title: '',
    author: '',
    category: 'primary',
    genre: 'fiction',
    description: '',
    publishedYear: '',
    pages: ''
  });

  useEffect(() => {
    // Simulate fetching books
    setTimeout(() => {
      const mockBooks = Array(10).fill().map((_, index) => ({
        id: index + 1,
        title: `Book Title ${index + 1}`,
        author: `Author ${index + 1}`,
        category: index % 3 === 0 ? 'primary' : index % 3 === 1 ? 'secondary' : 'college',
        genre: index % 5 === 0 ? 'fiction' : index % 5 === 1 ? 'non-fiction' : index % 5 === 2 ? 'science' : index % 5 === 3 ? 'history' : 'literature',
        publishedYear: 2000 + index,
        pages: 200 + (index * 10),
        status: index % 4 === 0 ? 'draft' : 'published'
      }));
      
      setBooks(mockBooks);
      setFilteredBooks(mockBooks);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (books.length === 0) return;
    
    const filtered = books.filter(book => 
      book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.author.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredBooks(filtered);
  }, [searchTerm, books]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleNewBookChange = (e) => {
    const { name, value } = e.target;
    setNewBook(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddBook = () => {
    // In a real app, this would be an API call
    const newBookWithId = {
      ...newBook,
      id: books.length + 1,
      status: 'draft'
    };
    
    setBooks(prev => [...prev, newBookWithId]);
    setShowAddModal(false);
    setNewBook({
      title: '',
      author: '',
      category: 'primary',
      genre: 'fiction',
      description: '',
      publishedYear: '',
      pages: ''
    });
  };

  const handleDeleteBook = (id) => {
    // In a real app, this would be an API call
    setBooks(prev => prev.filter(book => book.id !== id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Manage Books</h1>
              <p className="text-gray-600">Add, edit, or remove books from the library</p>
            </div>
            <Button 
              variant="primary"
              onClick={() => setShowAddModal(true)}
            >
              Add New Book
            </Button>
          </div>

          {/* Search and Filters */}
          <Card className="mb-8 p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-grow">
                <Input
                  label="Search Books"
                  type="text"
                  id="search"
                  placeholder="Search by title or author"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="flex items-end">
                <Button 
                  variant="secondary"
                  onClick={() => setSearchTerm('')}
                  className="mb-4 md:mb-0"
                >
                  Clear
                </Button>
              </div>
            </div>
          </Card>

          {/* Books Table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Author
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Genre
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Published
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBooks.length > 0 ? (
                    filteredBooks.map((book) => (
                      <motion.tr 
                        key={book.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{book.title}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{book.author}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-secondary-orange bg-opacity-10 text-secondary-orange">
                            {book.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {book.genre}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {book.publishedYear}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            book.status === 'published' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {book.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            className="text-primary hover:text-primary-dark mr-4"
                            onClick={() => {/* Edit book logic */}}
                          >
                            Edit
                          </button>
                          <button 
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleDeleteBook(book.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                        No books found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Add Book Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Add New Book</h2>
                <button 
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setShowAddModal(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Title"
                  type="text"
                  id="title"
                  name="title"
                  value={newBook.title}
                  onChange={handleNewBookChange}
                  required
                />
                
                <Input
                  label="Author"
                  type="text"
                  id="author"
                  name="author"
                  value={newBook.author}
                  onChange={handleNewBookChange}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={newBook.category}
                    onChange={handleNewBookChange}
                    className="input-field"
                  >
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="college">College</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="genre" className="block text-sm font-medium text-gray-700 mb-1">
                    Genre
                  </label>
                  <select
                    id="genre"
                    name="genre"
                    value={newBook.genre}
                    onChange={handleNewBookChange}
                    className="input-field"
                  >
                    <option value="fiction">Fiction</option>
                    <option value="non-fiction">Non-Fiction</option>
                    <option value="science">Science</option>
                    <option value="history">History</option>
                    <option value="literature">Literature</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Published Year"
                  type="number"
                  id="publishedYear"
                  name="publishedYear"
                  value={newBook.publishedYear}
                  onChange={handleNewBookChange}
                />
                
                <Input
                  label="Pages"
                  type="number"
                  id="pages"
                  name="pages"
                  value={newBook.pages}
                  onChange={handleNewBookChange}
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  value={newBook.description}
                  onChange={handleNewBookChange}
                  className="input-field"
                ></textarea>
              </div>
              
              <div className="flex justify-end gap-4">
                <Button 
                  variant="secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary"
                  onClick={handleAddBook}
                  disabled={!newBook.title || !newBook.author}
                >
                  Add Book
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ManageBooksPage; 